import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { ROUTES } from '../../../../../presentation/routes/Paths';
import { ServiceData } from '../../data/servicesData';
import '../style/service-detail.css';
import { trackPhoneClick } from '../../../../../presentation/components/email/utils/trackingUtils';

interface ServiceDetailSectionProps {
    service: ServiceData;
}

const ServiceDetailSection: React.FC<ServiceDetailSectionProps> = ({ service }) => {
    const { t, language } = useLanguage();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const serviceImageRef = useRef<HTMLDivElement>(null);
    const translation = t as any;
    const serviceDetails = translation.SERVICE_DETAILS?.[service.translationKey] || {};
    const isRevision = service.translationKey === 'roadyReview';
    const services = translation.SERVICES || {};
    
    // Scroll to service image section when component mounts or service changes
    useEffect(() => {
        if (serviceImageRef.current) {
            const yOffset = -100; // Offset for fixed navbar
            const element = serviceImageRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    }, [service.id]);
    
    // Force navbar to sticky mode when sidebar is open
    useEffect(() => {
        if (isSidebarOpen) {
            document.body.classList.add('mobile-menu-visible');
        } else {
            document.body.classList.remove('mobile-menu-visible');
        }
        return () => {
            document.body.classList.remove('mobile-menu-visible');
        };
    }, [isSidebarOpen]);
    
    // Get language-specific translations
    const getTranslation = (obj: any) => {
        if (!obj) return '';
        return typeof obj === 'string' ? obj : (obj[language] || obj.pt || obj.en || '');
    };
    
    const getArrayTranslation = (arr: any) => {
        if (!arr) return [];
        if (Array.isArray(arr)) {
            return arr.map(item => typeof item === 'string' ? item : getTranslation(item));
        }
        // Handle object with language keys containing arrays
        if (typeof arr === 'object' && arr !== null) {
            const langArray = arr[language] || arr.pt || arr.en;
            if (Array.isArray(langArray)) {
                return langArray;
            }
        }
        return [];
    };

    return (
        <>
            {/* Page Header */}
            <section className="page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: `url(${service.bgImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <h1>{getTranslation(serviceDetails.pageTitle) || getTranslation(services[service.translationKey]) || service.translationKey}</h1>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{getTranslation(translation.NAVIGATION?.start) || 'Home'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li><Link to="/oficina">{getTranslation(translation.NAVIGATION?.workshop) || 'Workshop'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{getTranslation(services[service.translationKey]) || service.translationKey}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Service Details */}
            <section className="service-details">
                <div className="container">
                    {/* Mobile Service Selection Toggle Button */}
                    <div className="service-details__mobile-filter-toggle">
                        <button 
                            type="button"
                            className="service-details__mobile-filter-btn"
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            aria-label="Toggle services menu"
                        >
                            <i className="fa fa-list"></i>
                            <span>{getTranslation(translation.SERVICES?.servicesTagline) || 'Our Services'}</span>
                        </button>
                    </div>

                    {/* Mobile Sidebar Overlay */}
                    {isSidebarOpen && (
                        <div 
                            className="service-details__mobile-sidebar-overlay"
                            onClick={() => setIsSidebarOpen(false)}
                        ></div>
                    )}

                    <div className="row">
                        {/* Sidebar */}
                        <div className={`col-xl-4 col-lg-5 service-details__sidebar-col ${isSidebarOpen ? 'service-details__sidebar-col--open' : ''}`}>
                            <div className="service-details__sidebar">
                                {/* Mobile Close Button */}
                                <button 
                                    type="button"
                                    className="service-details__sidebar-close"
                                    onClick={() => setIsSidebarOpen(false)}
                                    aria-label="Close services menu"
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                                <div className="service-details__services-box">
                                    <h3 className="service-details__services-title">
                                        {getTranslation(translation.SERVICES?.servicesTagline) || 'Our Services'}
                                    </h3>
                                    <ul className="service-details__services-list list-unstyled">
                                        {Object.entries({
                                            alignment: '/oficina/alinhamento',
                                            diagnosticTest: '/oficina/teste-diagnostico',
                                            roadyReview: '/oficina/revisoes-roady',
                                            shockAbsorbers: '/oficina/amortecedores',
                                            inspection: '/oficina/inspecao',
                                            generalMechanics: '/oficina/mecanica-geral',
                                            workshopServices: '/oficina/servicos-oficina',
                                            airConditioning: '/oficina/ar-condicionado',
                                            washing: '/oficina/lavagem',
                                            headlightRestoration: '/oficina/restauro-farois',
                                            batteries: '/oficina/baterias',
                                            brakes: '/oficina/travoes'
                                        }).map(([key, path]) => (
                                            <li key={key} className={service.translationKey === key ? 'active' : ''}>
                                                <Link 
                                                    to={path}
                                                    onClick={() => {
                                                        // Close sidebar on mobile when service is clicked
                                                        if (window.innerWidth <= 991) {
                                                            setIsSidebarOpen(false);
                                                        }
                                                    }}
                                                >
                                                    {getTranslation(services[key]) || key}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                
                                <div className="service-details__sidebar-contact">
                                    <div className="service-details__sidebar-contact-img">
                                        <div className="inner">
                                            <img loading="lazy" src="/assets/logo/contact/contact_us_phone.webp" alt="Roady Chaves" />
                                        </div>
                                    </div>
                                    <div className="service-details__sidebar-contact-content">
                                        <div className="icon">
                                            <span className="icon-phone-call"></span>
                                        </div>
                                        <h3>
                                            <a href="tel:+351276249290" onClick={() => trackPhoneClick('service_detail')}>
                                                {getTranslation(translation.CONTACT?.number) || '(+351) 276 249 290'}
                                            </a>
                                        </h3>
                                        <p dangerouslySetInnerHTML={{ __html: getTranslation(translation.CONTACT?.helpText) || 'If You Need Any Help<br>Contact With Us' }}></p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="col-xl-8 col-lg-7">
                            <div className="service-details__left">
                                <div className="service-details__img" ref={serviceImageRef}>
                                    <img loading="lazy" 
                                        src={service.image} 
                                        alt={services[service.translationKey] || service.translationKey}
                                        style={{ objectFit: 'cover', width: '100%', height: '400px' }}
                                    />
                                </div>
                                
                                <div className="service-details__content">
                                    {/* Header */}
                                    <div className="service-details__content-header">
                                        <div className="service-details__img-box-content-icon">
                                            <span className={service.icon}></span>
                                        </div>
                                        <h3 className="service-details__title-1">
                                            {getTranslation(serviceDetails.title) || getTranslation(services[service.translationKey])}
                                        </h3>
                                    </div>
                                    
                                    {/* Intro Section */}
                                    <div className="service-details__intro-section">
                                        <div className="service-details__intro-icon">
                                            <span className="icon-technician"></span>
                                        </div>
                                        <p className="service-details__text-intro">
                                            {getTranslation(serviceDetails.intro)}
                                        </p>
                                    </div>
                                    
                                    {isRevision ? (
                                        <>
                                            {/* O que fica feito numa revisão */}
                                            <div className="service-details__how-section">
                                                <div className="service-details__section-header">
                                                    <div className="service-details__section-icon">
                                                        <span className="icon-tools"></span>
                                                    </div>
                                                    <h2 className="service-details__section-title">
                                                        {getTranslation(serviceDetails.howTitle)}
                                                    </h2>
                                                </div>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.how)}
                                                </p>
                                            </div>

                                            {/* A garantia do seu carro mantém-se */}
                                            <div className="service-details__importance-section">
                                                <div className="service-details__section-header">
                                                    <div className="service-details__section-icon">
                                                        <span className="icon-guarantee"></span>
                                                    </div>
                                                    <h2 className="service-details__section-title">
                                                        {getTranslation(serviceDetails.warrantyTitle)}
                                                    </h2>
                                                </div>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.warrantyText)}
                                                </p>
                                            </div>

                                            {/* Quanto custa uma revisão */}
                                            <div className="service-details__importance-section">
                                                <div className="service-details__section-header">
                                                    <div className="service-details__section-icon">
                                                        <span className="icon-price-tag"></span>
                                                    </div>
                                                    <h2 className="service-details__section-title">
                                                        {getTranslation(serviceDetails.costTitle)}
                                                    </h2>
                                                </div>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.costText1)}
                                                </p>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.costText2)}
                                                </p>
                                            </div>

                                            {/* Quando fazer a revisão */}
                                            <div className="service-details__importance-section">
                                                <div className="service-details__section-header">
                                                    <div className="service-details__section-icon">
                                                        <span className="icon-guarantee"></span>
                                                    </div>
                                                    <h2 className="service-details__section-title">
                                                        {getTranslation(serviceDetails.importanceTitle)}
                                                    </h2>
                                                </div>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.importance)}
                                                </p>
                                            </div>

                                            {/* Mudança de óleo e filtro */}
                                            <div className="service-details__how-section">
                                                <div className="service-details__section-header">
                                                    <div className="service-details__section-icon">
                                                        <span className="icon-oil"></span>
                                                    </div>
                                                    <h2 className="service-details__section-title">
                                                        {getTranslation(serviceDetails.oilChangeTitle)}
                                                    </h2>
                                                </div>
                                                <p className="service-details__text-content">
                                                    {getTranslation(serviceDetails.oilChangeText)}
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                    {/* Importance Section */}
                                    <div className="service-details__importance-section">
                                        <div className="service-details__section-header">
                                            <div className="service-details__section-icon">
                                                <span className="icon-guarantee"></span>
                                            </div>
                                            <h3 className="service-details__section-title">
                                                {getTranslation(serviceDetails.importanceTitle) || 'Importância do serviço'}
                                            </h3>
                                        </div>
                                        <p className="service-details__text-content">
                                            {getTranslation(serviceDetails.importance)}
                                        </p>
                                    </div>
                                    
                                    {/* How Section */}
                                    <div className="service-details__how-section">
                                        <div className="service-details__section-header">
                                            <div className="service-details__section-icon">
                                                <span className="icon-tools"></span>
                                            </div>
                                            <h3 className="service-details__section-title">
                                                {getTranslation(serviceDetails.howTitle) || 'Como é feito no Roady'}
                                            </h3>
                                        </div>
                                        <p className="service-details__text-content">
                                            {getTranslation(serviceDetails.how)}
                                        </p>
                                    </div>
                                        </>
                                    )}

                                    {/* Benefits Section */}
                                    <div className="service-details__benefits-section">
                                        <div className="service-details__section-header">
                                            <div className="service-details__section-icon">
                                                <span className="icon-satisfaction"></span>
                                            </div>
                                            <h3 className="service-details__section-title">
                                                {getTranslation(serviceDetails.advantagesTitle) || 'Vantagens Roady Chaves:'}
                                            </h3>
                                        </div>
                                        <ul className="service-details__benefits-list list-unstyled">
                                            {getArrayTranslation(serviceDetails.advantages).map((advantage: string, index: number) => (
                                                <li key={index}>
                                                    <span className="benefit-icon">
                                                        <i className="fas fa-check-circle"></i>
                                                    </span>
                                                    <span className="benefit-text">{advantage}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    
                                    {/* CTA Section */}
                                    <div className="service-details__cta-section">
                                        <div className="service-details__cta-icon">
                                            <span className="icon-phone-call"></span>
                                        </div>
                                        <p className="service-details__cta-text">
                                            {getTranslation(serviceDetails.cta)}
                                        </p>
                                        <div className="service-details__btn-box">
                                            <Link to={`${ROUTES.BUDGET}?service=${service.budgetParam}`} className="thm-btn">
                                                {getTranslation(translation.BUTTONS?.requestQuote) || 'Peça orçamento'}
                                                <span><i className="icon-next"></i></span>
                                            </Link>
                                            <a
                                                href="tel:+351276249290"
                                                className="thm-btn service-details__call-btn"
                                                onClick={() => trackPhoneClick('service_detail')}
                                            >
                                                {getTranslation(translation.BUTTONS?.callUsNow) || 'Fale connosco'}
                                                <span><i className="icon-phone-call"></i></span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ServiceDetailSection;

